import { Component, Input, OnInit, ChangeDetectionStrategy } from "@angular/core";
import { DatePipe } from "@angular/common";
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import { AppService } from "@mypxplat/xplat/web/core";

type OsFilter = "mac" | "win";

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: "myp-download-versions-modal",
  templateUrl: "download-versions-modal.component.html",
  standalone: false,
  providers: [DatePipe],
})
export class DownloadVersionsModalComponent implements OnInit {
  @Input() title: string = "Versions";
  @Input() versions: any[] = [];

  public osOptions: Array<{ id: OsFilter; name: string; selected?: boolean }> = [
    { id: "mac", name: "Mac" },
    { id: "win", name: "Windows" },
  ];
  public selectedOs: OsFilter = "mac";
  public filteredVersions: any[] = [];

  constructor(
    public activeModal: NgbActiveModal,
    private _appService: AppService,
    private _datePipe: DatePipe
  ) {}

  ngOnInit() {
    // Mac users outnumber Windows visitors on this app per analytics, so anything other than
    // a detected "win" defaults to the Mac tab (mirrors AppService.os's own mac-default).
    this.selectedOs = this._appService.os === "win" ? "win" : "mac";
    this.osOptions = this.osOptions.map((opt) => ({ ...opt, selected: opt.id === this.selectedOs }));
    this._applyFilter();
  }

  onOsSelected(option: { id: OsFilter }) {
    this.selectedOs = option.id;
    this._applyFilter();
  }

  download(entry: any) {
    if (entry?.url) window.open(entry.url, "_blank");
  }

  private _applyFilter() {
    // `myp-list-item`'s `subData` renders raw strings as-is (no pipes in its template), so the
    // version/date formatting has to happen here rather than in the modal's own HTML.
    this.filteredVersions = (this.versions || [])
      .filter((v) => this._matchesOs(v, this.selectedOs))
      .map((v) => ({
        ...v,
        subData: [`${v.beta ? "BETA v" : "v"}${v.version || ""}`, v.releaseDate ? this._datePipe.transform(v.releaseDate) : ""].filter(Boolean),
      }));
  }

  /**
   * A version matches the selected OS when its `platform` field (a real guess, not the
   * "other" catch-all) starts with the filter name — this correctly matches "win-arm64"/
   * "winarm64" to the Windows filter, and correctly hides Android/Linux/Flatpak entries from
   * both filters, something the old operatingSystemInfo-only check never did (an Android APK's
   * compat string doesn't match the win/mac regexes either, so it was accidentally hidden the
   * same way — this makes it deliberate). Falls back to the old operatingSystemInfo regex
   * match when `platform` is "other" or missing (legacy-sourced entries before `platform` was
   * added never had operatingSystemInfo either — unknown → show for both, so we don't hide
   * items nothing has tagged).
   */
  private _matchesOs(entry: any, os: OsFilter): boolean {
    const platform = typeof entry?.platform === "string" ? entry.platform.toLowerCase() : "";
    if (platform && platform !== "other") return platform.startsWith(os);

    const infos: any[] = entry?.operatingSystemInfo || [];
    if (!infos.length) return true;
    const winRe = /win(dows|32|64)?/i;
    const macRe = /mac(os)?|osx|os x/i;
    return infos.some((info: any) => {
      const s = `${info?.compatibility || ""} ${info?.name || ""} ${info?.version || ""}`;
      if (os === "win") return winRe.test(s);
      return macRe.test(s);
    });
  }
}
